import "../../styles/preCacheADMobile.scss";

import { Col } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import React, { useContext, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import ImagesRow from "@/components/Commons/ImagesRow";
import { StepBodyStyledText } from "@/components/Commons/StepBodyStyledText";
import { getOS, OPERATING_SYSTEM } from "@/helpers/os-helper";
import { AppContext } from "@/stores/appStore";
import { ActionType } from "@/stores/constants";

import PreCacheADLogo from "../../assets/images/preCacheADLogo.png";
import { StepListLiterals, StepListMac, StepListWindows } from "./StepLists";

/**
 * A step to help NHs to install the Pre-Cache AD Mobile app(Mac users only).
 */
export default function PreCacheADMobile(): JSX.Element {
  const intl = useIntl();
  const { dispatch } = useContext(AppContext);
  const { state } = useContext(AppContext);
  const { amazonUsername } = state;

  /**
   * Set the refPage to provide context to IT Support should the new hire initiate
   * a chat with support
   */
  useEffect(() => {
    dispatch({
      type: ActionType.SET_CHAT_SUPPORT_REF_PAGE,
      chatSupportRefPage: "ezo-install-precache",
    });
  }, []);

  const fontWeight = "regular";

  const isMac = getOS() === OPERATING_SYSTEM.MAC;

  const formatters = {
    b: (chunk: unknown) => <b>{chunk}</b>,
    logo: (
      <img
        width="20"
        height="19"
        src={PreCacheADLogo}
        id="preCacheADLogo"
        alt={intl.formatMessage({
          id: "preCacheADMobile-logo-altText",
        })}
      />
    ),
    username: amazonUsername,
    ...StepListLiterals,
  };

  /**
   * Render all steps needed to install Pre-Cache AD Mobile.
   */
  const renderStepList = (): JSX.Element[] => {
    const steps = isMac ? StepListMac : StepListWindows;
    return steps.map((stepList, index) => (
      <StepBodyStyledText key={stepList.stepLocaleId + "bodytextelement"}>
        <Text
          className="stepList-text"
          key={stepList.stepLocaleId}
          fontWeight={fontWeight}
          dataTestId={`preCacheStep${index + 1}`}
        >
          <li>
            <FormattedMessage id={stepList.stepLocaleId} values={formatters} />
            {stepList.children && stepList.children}
            {stepList.image ? (
              <Col padding={{ top: "S300" }}>
                <ImagesRow images={[stepList.image]} rowPadding="S400" />
              </Col>
            ) : null}
          </li>
        </Text>
      </StepBodyStyledText>
    ));
  };

  return (
    <Col gridGap="S300">
      <StepBodyStyledText>
        <Text fontWeight={fontWeight} dataTestId="preCacheIntroText">
          <FormattedMessage id="preCacheADMobile-expander-body-intro" />
        </Text>
      </StepBodyStyledText>
      <StepBodyStyledText>
        <Text fontWeight={fontWeight}>
          <ol className="stepLists">{renderStepList()}</ol>
        </Text>
      </StepBodyStyledText>
      {isMac && (
        <StepBodyStyledText>
          <Col gridGap={"S400"}>
            <Text fontWeight={fontWeight}>
              <FormattedMessage
                values={formatters}
                id="preCacheADMobile-expander-troubleshoot-mac-1"
              />
            </Text>
            <Text fontWeight={fontWeight}>
              <FormattedMessage
                values={formatters}
                id="preCacheADMobile-expander-troubleshoot-mac-2"
              />
            </Text>
          </Col>
        </StepBodyStyledText>
      )}
    </Col>
  );
}
